/*
  Agregar al carrito desde la parrilla, sin abrir el cajon.

  Tres decisiones que explican la forma del codigo:

  1. El estado de las tarjetas NO viene de Liquid. Las paginas de coleccion se
     sirven cacheadas y compartidas entre visitantes, asi que la cantidad se
     hidrata con una sola lectura de /cart.js al cargar.

  2. La interfaz se actualiza antes que el servidor. Quien pulsa «+» cinco
     veces seguidas no puede esperar cinco viajes de red; se pinta el numero al
     instante y se manda una sola peticion con la cantidad final.

  3. Las peticiones al carrito van en fila. Shopify responde con el carrito
     entero, y dos respuestas cruzadas dejarian el estado en la mas vieja.
*/

const HZ_ESPERA = 400;
const HZ_SECCIONES = 'cart-drawer,cart-icon-bubble';

/* varianteId -> { clave, cantidad }. `clave` es la line item key de Shopify,
   estable durante toda la vida de la linea; es lo que acepta /cart/change.js. */
let hzLineas = new Map();
const hzInstancias = new Set();
const hzTemporizadores = new Map();

/* Variantes que el cliente ya toco y cuyo cambio aun no ha confirmado el
   servidor. Sin esta lista, la respuesta de un producto borraba la intencion
   pendiente de otro: pulsar «+» en dos tarjetas seguidas perdia la segunda. */
const hzPendientes = new Set();

let hzCadena = Promise.resolve();
let hzTotal = 0;
let hzUnidades = 0;

const hzDinero = (centavos) =>
  '$' + Math.round(centavos / 100).toLocaleString('es-CO');

function hzCantidad(variante) {
  return hzLineas.get(variante)?.cantidad || 0;
}

function hzPintarTodo() {
  hzInstancias.forEach((el) => el.pintar());
  hzPintarPildora();
}

/* ── Lectura inicial del carrito ─────────────────────────────────────────── */

async function hzLeerCarrito() {
  try {
    const carrito = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
    hzAbsorber(carrito);
  } catch (e) {
    /* Sin carrito legible las tarjetas se quedan en cero, que es el estado
       seguro: pulsar «+» seguira agregando bien. */
  }
  hzPintarTodo();
}

/* Sustituye el estado completo por el que acaba de devolver Shopify. No se
   fusiona: si otra pestana o el cajon cambiaron el carrito, manda el servidor. */
function hzAbsorber(carrito) {
  if (!carrito || !Array.isArray(carrito.items)) return;

  const previas = hzLineas;
  const nuevas = new Map();
  carrito.items.forEach((linea) => {
    nuevas.set(String(linea.variant_id), { clave: linea.key, cantidad: linea.quantity });
  });

  let unidades = carrito.item_count || 0;

  /* Lo pendiente sobrevive al relevo: es lo que el cliente pulso y todavia no
     ha salido. La clave de linea si se toma del servidor, que es quien la
     asigna. */
  hzPendientes.forEach((variante) => {
    const local = previas.get(variante);
    if (!local) return;
    const servidor = nuevas.get(variante);
    unidades += local.cantidad - (servidor?.cantidad || 0);
    nuevas.set(variante, {
      clave: servidor?.clave || local.clave,
      cantidad: local.cantidad,
    });
  });

  hzLineas = nuevas;
  hzTotal = carrito.total_price || 0;
  hzUnidades = Math.max(0, unidades);
}

/* ── Envio ───────────────────────────────────────────────────────────────── */

function hzAjustar(variante, delta) {
  const linea = hzLineas.get(variante) || { clave: null, cantidad: 0 };
  linea.cantidad = Math.max(0, linea.cantidad + delta);
  hzLineas.set(variante, linea);
  hzPendientes.add(variante);

  /* La pildora mueve el contador al instante, pero deja el importe como
     estaba: adivinarlo sin conocer el precio de la linea daria una cifra
     falsa. El servidor lo corrige unas decimas despues. */
  hzUnidades = Math.max(0, hzUnidades + delta);
  hzPintarTodo();
  hzProgramar(variante);
}

function hzProgramar(variante) {
  clearTimeout(hzTemporizadores.get(variante));
  hzTemporizadores.set(
    variante,
    setTimeout(() => {
      hzTemporizadores.delete(variante);
      hzCadena = hzCadena.then(() => hzEnviar(variante)).catch(() => {});
    }, HZ_ESPERA)
  );
}

async function hzEnviar(variante) {
  const linea = hzLineas.get(variante);
  if (!linea) return;

  const destino = linea.cantidad;
  const raiz = window.Shopify?.routes?.root || '/';
  hzInstancias.forEach((el) => el.marcarEnvio(variante, true));

  try {
    let respuesta;
    if (linea.clave) {
      respuesta = await fetch(`${raiz}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: linea.clave, quantity: destino, sections: HZ_SECCIONES }),
      });
    } else {
      if (destino === 0) return;
      respuesta = await fetch(`${raiz}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variante), quantity: destino }],
          sections: HZ_SECCIONES,
        }),
      });
    }

    const datos = await respuesta.json();

    if (!respuesta.ok) {
      /* Lo mas comun aqui es pedir mas unidades de las que hay en inventario.
         Shopify lo rechaza entero, asi que la unica salida honesta es releer
         el carrito y mostrar lo que de verdad quedo. */
      await hzLeerCarrito();
      hzAvisar(datos?.description || datos?.message);
      return;
    }

    hzRefrescarSecciones(datos.sections);

    /* Si mientras viajaba la peticion el cliente volvio a pulsar, hay otro
       envio programado y la intencion sigue viva. */
    if (!hzTemporizadores.has(variante)) hzPendientes.delete(variante);

    if (Array.isArray(datos.items) && datos.items.length && datos.items[0].key && !datos.item_count) {
      /* Respuesta de add.js: trae las lineas agregadas, no el carrito. Se
         guarda la clave y se relee el total aparte. Solo ocurre en el primer
         «+» de cada producto. */
      hzLineas.set(variante, {
        clave: datos.items[0].key,
        cantidad: datos.items[0].quantity,
      });
      await hzLeerCarrito();
      return;
    }

    /* Respuesta de change.js: es el carrito completo. */
    hzAbsorber(datos);
  } catch (e) {
    await hzLeerCarrito();
  } finally {
    hzInstancias.forEach((el) => el.marcarEnvio(variante, false));
    hzPintarTodo();
  }
}

function hzAvisar(texto) {
  if (!texto) return;
  const previo = document.querySelector('.hz-aviso');
  if (previo) previo.remove();
  const caja = document.createElement('div');
  caja.className = 'hz-aviso';
  caja.setAttribute('role', 'status');
  caja.textContent = texto;
  Object.assign(caja.style, {
    position: 'fixed',
    left: '50%',
    bottom: '8rem',
    transform: 'translateX(-50%)',
    zIndex: '5',
    maxWidth: '32rem',
    background: 'rgb(var(--color-foreground))',
    color: 'rgb(var(--color-background))',
    fontSize: '1.3rem',
    padding: '1rem 1.6rem',
    borderRadius: '0.8rem',
  });
  document.body.appendChild(caja);
  setTimeout(() => caja.remove(), 5000);
}

/* ── Cajon y burbuja del encabezado ──────────────────────────────────────── */

function hzTrozo(html, selector) {
  const nodo = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  return nodo ? nodo.innerHTML : null;
}

/* Se actualizan aunque no se abran: el cajon tiene que estar al dia para
   cuando el cliente decida abrirlo desde la pildora o el encabezado. */
function hzRefrescarSecciones(secciones) {
  if (!secciones) return;

  const burbuja = document.getElementById('cart-icon-bubble');
  if (burbuja && secciones['cart-icon-bubble']) {
    const dentro = hzTrozo(secciones['cart-icon-bubble'], '.shopify-section');
    if (dentro !== null) burbuja.innerHTML = dentro;
  }

  const cajon = document.getElementById('CartDrawer');
  if (cajon && secciones['cart-drawer']) {
    const doc = new DOMParser().parseFromString(secciones['cart-drawer'], 'text/html');
    const nuevo = doc.querySelector('#CartDrawer');
    const dentro = nuevo ? nuevo.innerHTML : null;
    if (dentro !== null) {
      cajon.innerHTML = dentro;
      /* La clase is-empty vive en <cart-drawer>, FUERA de lo reemplazado. Si
         la pagina cargo con el carrito vacio, se quedaba puesta y el primer
         producto agregado no se veia en el cajon hasta recargar (2026-09-30).
         Se copia del HTML recien renderizado, que sabe si hay productos. */
      const vacio = doc.querySelector('cart-drawer')?.classList.contains('is-empty');
      const cd = document.querySelector('cart-drawer');
      if (cd && typeof vacio === 'boolean') cd.classList.toggle('is-empty', vacio);
      /* Al reemplazar el contenido se pierde el oyente del velo; Dawn lo
         vuelve a poner en renderContents y aqui hay que hacer lo mismo. */
      const elemento = document.querySelector('cart-drawer');
      const velo = cajon.querySelector('#CartDrawer-Overlay');
      if (elemento && velo) velo.addEventListener('click', elemento.close.bind(elemento));
    }
  }
}

/* ── Pildora ─────────────────────────────────────────────────────────────── */

function hzPintarPildora() {
  const pildora = document.querySelector('[data-hz-pildora]');
  if (!pildora) return;

  if (hzUnidades <= 0) {
    pildora.toggleAttribute('hidden', true);
    return;
  }
  pildora.toggleAttribute('hidden', false);
  pildora.querySelector('[data-hz-pildora-n]').textContent = hzUnidades;
  pildora.querySelector('[data-hz-pildora-total]').textContent = hzTotal ? hzDinero(hzTotal) : '';
}

document.addEventListener('click', (evento) => {
  const pildora = evento.target.closest('[data-hz-pildora]');
  if (!pildora) return;
  /* Con el cajon activo se abre ahi mismo; sin el, el enlace a /cart hace su
     trabajo sin que haga falta interceptarlo. */
  const cajon = document.querySelector('cart-drawer');
  if (cajon && typeof cajon.open === 'function') {
    evento.preventDefault();
    cajon.open(pildora);
  }
});

/* ── El elemento de la tarjeta ───────────────────────────────────────────── */

class HorecaAgregar extends HTMLElement {
  connectedCallback() {
    this.variante = this.dataset.variante;
    this.mas = this.querySelector('[data-hz="mas"]');
    this.paso = this.querySelector('[data-hz="paso"]');
    this.numero = this.querySelector('[data-hz="n"]');
    this.iconoMenos = this.querySelector('[data-hz="icono-menos"]');
    this.iconoQuitar = this.querySelector('[data-hz="icono-quitar"]');

    hzInstancias.add(this);
    this.addEventListener('click', this.alPulsar.bind(this));
    this.pintar();
  }

  disconnectedCallback() {
    hzInstancias.delete(this);
  }

  alPulsar(evento) {
    const boton = evento.target.closest('[data-hz]');
    if (!boton) return;

    /* La tarjeta entera es un enlace a la ficha; sin esto cada clic navegaria. */
    evento.preventDefault();
    evento.stopPropagation();

    /* Un producto con varias presentaciones no se puede resolver desde aqui:
       el «+» no sabe cual quiere el cliente, asi que lleva a la ficha. Hoy el
       catalogo no tiene ninguno, pero el dia que lo tenga no debe agregar
       la variante equivocada en silencio. */
    if (this.dataset.varias === 'true') {
      window.location.href = this.dataset.url;
      return;
    }

    const accion = boton.dataset.hz;
    if (accion === 'mas' || accion === 'sumar') hzAjustar(this.variante, 1);
    else if (accion === 'menos') hzAjustar(this.variante, -1);
  }

  marcarEnvio(variante, activo) {
    if (variante !== this.variante) return;
    if (activo) this.setAttribute('data-enviando', '');
    else this.removeAttribute('data-enviando');
  }

  pintar() {
    if (!this.mas || !this.paso) return;
    const n = hzCantidad(this.variante);

    /* toggleAttribute y no la propiedad .hidden: esa esta definida en
       HTMLElement, y estos iconos son <svg>, donde asignarla no cambia nada. */
    this.mas.toggleAttribute('hidden', n > 0);
    this.paso.toggleAttribute('hidden', n === 0);
    this.numero.textContent = n;

    /* Con una sola unidad, el boton de restar borra la linea: el icono de
       papelera lo dice antes de pulsarlo. */
    if (this.iconoMenos && this.iconoQuitar) {
      this.iconoMenos.toggleAttribute('hidden', n === 1);
      this.iconoQuitar.toggleAttribute('hidden', n !== 1);
    }
  }
}

if (!customElements.get('horeca-agregar')) {
  customElements.define('horeca-agregar', HorecaAgregar);
}

hzLeerCarrito();

/* El cajon lateral y la pagina del carrito emiten esto al cambiar cantidades;
   sin escucharlo, las tarjetas mostrarian el estado viejo. */
document.addEventListener('cart:refresh', hzLeerCarrito);
if (typeof subscribe === 'function') {
  subscribe(window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update', hzLeerCarrito);
}
